import { type PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type StorageType, getStorageByType } from 'utils/storage';

import type { BaseButtonItem } from './model';

type ButtonGroupContextProps<T extends BaseButtonItem> = {
  buttonGroup: T[];
  onButtonClick: (button: T['type']) => void;
  activateButtonItem: T | undefined;
};

const ButtonGroupContext = createContext<ButtonGroupContextProps<BaseButtonItem>>({
  buttonGroup: [],
  onButtonClick: () => {},
  activateButtonItem: undefined,
});

type ButtonGroupProviderProps<T extends BaseButtonItem> = {
  buttonGroup: T[];
  storage?: {
    type: StorageType;
    name: string;
  };
};
export function ButtonGroupProvider<T extends BaseButtonItem>({
  children,
  buttonGroup,
  storage,
}: PropsWithChildren<ButtonGroupProviderProps<T>>) {
  const { t } = useTranslation();
  const [internalButtonGroup] = useState<T[]>(buttonGroup);

  const [activateButtonItem, setActivateButtonItem] = useState<T>(() => {
    const defaultValue = internalButtonGroup[0];
    const isUnuseStorage = !storage;
    if (isUnuseStorage) return defaultValue;

    try {
      const storageObject = getStorageByType(storage.type);
      const storedData = storageObject.getItem(storage.name);

      return storedData ? (JSON.parse(storedData) as T) : defaultValue;
    } catch (error) {
      console.error(`Failed to parse stored button state: ${error}`);
      return defaultValue;
    }
  });

  useEffect(() => {
    if (!storage) return;

    try {
      const storageObject = getStorageByType(storage.type);
      storageObject.setItem(storage.name, JSON.stringify(activateButtonItem));
    } catch (error) {
      console.error(`Failed to store button state: ${error}`);
    }
  }, [activateButtonItem, storage]);

  const onButtonClick = (clickedType: T['type']) => {
    const foundButton = internalButtonGroup.find((item) => item.type === clickedType);
    if (!foundButton) {
      return;
    }
    setActivateButtonItem(foundButton);
  };

  return (
    <ButtonGroupContext.Provider
      value={{
        buttonGroup: internalButtonGroup.map((button) => ({ ...button, label: t(button.label) })),
        onButtonClick,
        activateButtonItem,
      }}
    >
      {children}
    </ButtonGroupContext.Provider>
  );
}

export const useButtonGroupContext = () => {
  const context = useContext(ButtonGroupContext);
  if (!context) {
    throw new Error('ButtonGroupContext must be used within a ButtonGroupProvider');
  }
  return context;
};
